import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "@shared/ui/resizable";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { Label } from "@shared/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { ScrollArea } from "@shared/ui/scroll-area";
import { Separator } from "@shared/ui/separator";
import {
  Move,
  RotateCcw,
  Save,
  Download,
  Trash2,
  Copy,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  Grid3X3,
  Maximize2,
  MousePointer,
  FileText,
  Layout,
  Minimize2,
} from "lucide-react";
import { useCanvasTemplate } from "../../hooks";
import { CanvasComponent, ComponentStyle } from "../../types";
import FieldMappingDialog from "../fieldMappingDialog";
import { logEvent } from "@shared/analytics/analytics";
import { useNavigate } from "react-router-dom";
import { toast } from "@shared/hooks/use-toast";

// Page size presets
const PAGE_SIZES = {
  a4: { width: 794, height: 1123, label: "A4 (210×297mm)" },
  letter: { width: 816, height: 1056, label: 'Letter (8.5×11")' },
  legal: { width: 816, height: 1344, label: 'Legal (8.5×14")' },
  custom: { width: 800, height: 1000, label: "Custom" },
};

interface BillCanvasDesignerProps {
  baseTemplateId?: string;
  templateId?: string;
}

const BillCanvasDesigner: React.FC<BillCanvasDesignerProps> = ({
  baseTemplateId,
  templateId,
}) => {

  const navigate = useNavigate();
  // Hook integration
  const {
    components,
    setComponents,
    availableComponents,
    availableTemplates,
    templateName,
    setTemplateName,
    isLoading,
    isLoadingDetail,
    error,
    queryErrorDetail,
    saveTemplate,
    isSaving,
    updateTemplate,
    isUpdating,
    templateId: existingTemplateId,
    loadTemplate,
    componentConfig,
    setComponentConfig,
    isLoadingBaseTemplate,
  } = useCanvasTemplate(templateId);

  // Local state
  const [pageSize, setPageSize] = useState("a4");
  const [customWidth, setCustomWidth] = useState(800);
  const [customHeight, setCustomHeight] = useState(1000);
  const [showGrid, setShowGrid] = useState(true);
  const [snapToGrid, setSnapToGrid] = useState(true);
  const [selectedComponent, setSelectedComponent] = useState<string | null>(
    null
  );
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState("");
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  const [componentStartPos, setComponentStartPos] = useState<{
    x: number;
    y: number;
    width?: number;
    height?: number;
  }>({ x: 0, y: 0 });
  const [tool, setTool] = useState<"select" | "move">("select");
  const [activeTab, setActiveTab] = useState<"canvas" | "preview">("canvas");
  const [previewExpanded, setPreviewExpanded] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [showFieldMapping, setShowFieldMapping] = useState(false);
  const [selectedComponentForMapping, setSelectedComponentForMapping] =
    useState<string | null>(null);
  const [localTemplateName, setLocalTemplateName] = useState(templateName);

  const canvasRef = useRef<HTMLDivElement>(null);
  const previewRef = useRef<HTMLIFrameElement>(null);
  const gridSize = 10;

  // Sync local template name with hook template name
  useEffect(() => {
    setLocalTemplateName(templateName);
  }, [templateName]);

  const currentPageSize =
    pageSize === "custom"
      ? { width: customWidth, height: customHeight }
      : PAGE_SIZES[pageSize as keyof typeof PAGE_SIZES];

  const snapToGridValue = (value: number) => {
    return snapToGrid ? Math.round(value / gridSize) * gridSize : value;
  };

  // Helper function to extract fields from HTML
  const extractFieldsFromHtml = (html: {}): string[] => {
    const fields: string[] = [];

    for (const key in html) {
      if (Object.prototype.hasOwnProperty.call(html, key)) {
        fields.push(key);
      }
    }

    return fields;
  };

  // Field Mapping handler
  const handleFieldMapping = (componentId: string, mappings: any) => {
    const updatedConfig = { ...componentConfig };
    updatedConfig[componentId] = {
      ...updatedConfig[componentId],
      fields: mappings,
    };
    setComponentConfig(updatedConfig);
  };

  // Template selection handler
  const handleTemplateSelect = (value: string) => {
    setSelectedTemplateId(value);
    if (value) {
      loadTemplate(value);
    }
  };

  const handleTemplateNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setLocalTemplateName(newName);
    setTemplateName(newName);
  };

  const addComponent = (componentDef: any) => {
    const newComponent: CanvasComponent = {
      id: `component-${Date.now()}`,
      componentId: componentDef.id,
      type: componentDef.componentName,
      x: snapToGridValue(50),
      y: snapToGridValue(50 + components.length * 20),
      width: componentDef.defaultSize?.width || 300,
      height: componentDef.defaultSize?.height || 150,
      zIndex: components.length + 1,
      rotation: 0,
      isVisible: true,
      isLocked: false,
      content: componentDef,
      style: {
        backgroundColor: "#ffffff",
        borderColor: "#e5e7eb",
        borderWidth: 1,
        borderStyle: "solid",
        borderRadius: 0,
        padding: 10,
        fontSize: 14,
        color: "#374151",
        textAlign: "left",
      },
    };

    setComponents([...components, newComponent]);
    setSelectedComponent(newComponent.id);
  };

  const updateComponent = (id: string, updates: Partial<CanvasComponent>) => {
    setComponents(
      components.map((comp) =>
        comp.id === id ? { ...comp, ...updates } : comp
      )
    );
    updatePreview();
  };

  const updateComponentStyle = (
    id: string,
    styleUpdates: Partial<ComponentStyle>
  ) => {
    setComponents(
      components.map((comp) =>
        comp.id === id
          ? { ...comp, style: { ...comp.style, ...styleUpdates } }
          : comp
      )
    );
    updatePreview();
  };

  const deleteComponent = (id: string) => {
    setComponents(components.filter((comp) => comp.id !== id));
    if (selectedComponent === id) {
      setSelectedComponent(null);
    }
  };

  const duplicateComponent = (id: string) => {
    const component = components.find((comp) => comp.id === id);
    if (component) {
      const newComponent = {
        ...component,
        id: `component-${Date.now()}`,
        x: component.x + 20,
        y: component.y + 20,
        zIndex: Math.max(...components.map((c) => c.zIndex)) + 1,
      };
      setComponents([...components, newComponent]);
      setSelectedComponent(newComponent.id);
    }
  };

  const handleCanvasClick = (e: React.MouseEvent) => {
    // Only deselect if clicking directly on canvas, not on a component
    if (e.target === e.currentTarget) {
      setSelectedComponent(null);
    }
  };

  const handleComponentClick = (e: React.MouseEvent, componentId: string) => {
    e.stopPropagation(); // Prevent canvas click from firing

    const component = components.find((c) => c.id === componentId);
    if (!component || component.isLocked) return;

    setSelectedComponent(componentId);
  };

  const handleMouseDown = (
    e: React.MouseEvent,
    componentId: string,
    isResize = false,
    handle = ""
  ) => {
    e.preventDefault();
    e.stopPropagation();

    const component = components.find((c) => c.id === componentId);
    if (!component || component.isLocked) return;

    setSelectedComponent(componentId);

    const mouseX = e.clientX;
    const mouseY = e.clientY;

    if (isResize && handle) {
      setIsResizing(true);
      setResizeHandle(handle);
      setDragStartPos({ x: mouseX, y: mouseY });
      setComponentStartPos({
        x: component.x,
        y: component.y,
        width: component.width,
        height: component.height,
      });
    } else if (tool === "move" || e.shiftKey) {
      setIsDragging(true);
      setDragStartPos({ x: mouseX, y: mouseY });
      setComponentStartPos({
        x: component.x,
        y: component.y,
        width: component.width,
        height: component.height,
      });
    }
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!selectedComponent || (!isDragging && !isResizing)) return;

      const component = components.find((c) => c.id === selectedComponent);
      if (!component) return;

      const deltaX = e.clientX - dragStartPos.x;
      const deltaY = e.clientY - dragStartPos.y;

      if (isDragging) {
        const newX = snapToGridValue(componentStartPos.x + deltaX);
        const newY = snapToGridValue(componentStartPos.y + deltaY);

        updateComponent(selectedComponent, {
          x: Math.max(
            0,
            Math.min(newX, currentPageSize.width - component.width)
          ),
          y: Math.max(
            0,
            Math.min(newY, currentPageSize.height - component.height)
          ),
        });
      } else if (
        isResizing &&
        componentStartPos.width !== undefined &&
        componentStartPos.height !== undefined
      ) {
        let newWidth = componentStartPos.width;
        let newHeight = componentStartPos.height;
        let newX = componentStartPos.x;
        let newY = componentStartPos.y;

        switch (resizeHandle) {
          case "se":
            newWidth = componentStartPos.width + deltaX;
            newHeight = componentStartPos.height + deltaY;
            break;
          case "sw":
            newWidth = componentStartPos.width - deltaX;
            newX = componentStartPos.x + deltaX;
            newHeight = componentStartPos.height + deltaY;
            break;
          case "ne":
            newWidth = componentStartPos.width + deltaX;
            newHeight = componentStartPos.height - deltaY;
            newY = componentStartPos.y + deltaY;
            break;
          case "nw":
            newWidth = componentStartPos.width - deltaX;
            newHeight = componentStartPos.height - deltaY;
            newX = componentStartPos.x + deltaX;
            newY = componentStartPos.y + deltaY;
            break;
        }

        // Ensure minimum size
        newWidth = Math.max(50, newWidth);
        newHeight = Math.max(50, newHeight);

        // Snap to grid
        newX = snapToGridValue(newX);
        newY = snapToGridValue(newY);
        newWidth = snapToGridValue(newWidth);
        newHeight = snapToGridValue(newHeight);

        updateComponent(selectedComponent, {
          x: Math.max(0, newX),
          y: Math.max(0, newY),
          width: newWidth,
          height: newHeight,
        });
      }
    },
    [
      isDragging,
      isResizing,
      selectedComponent,
      components,
      dragStartPos,
      componentStartPos,
      currentPageSize,
      snapToGrid,
      resizeHandle,
    ]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeHandle("");
  }, []);

  useEffect(() => {
    if (isDragging || isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, isResizing, handleMouseMove, handleMouseUp]);

  // Preview Update
  const updatePreview = useCallback(() => {
    if (activeTab !== "preview" || !previewRef.current) return;

    const previewHtml = generatePreviewHtml();
    const previewDoc = previewRef.current.contentDocument;
    if (previewDoc) {
      previewDoc.open();
      previewDoc.write(previewHtml);
      previewDoc.close();
    }
  }, [components, currentPageSize, activeTab, componentConfig]);

  useEffect(() => {
    updatePreview();
  }, [updatePreview, activeTab]);

  const generatePreviewHtml = () => {
    const sortedComponents = [...components].sort(
      (a, b) => a.zIndex - b.zIndex
    );

    // Sample data for preview
    const sampleData = {
      utility: {
        logo_url: "/api/placeholder/60/60",
        tagline: "PULEGA O LE SUAVAI",
      },
      customer: {
        name: "John Doe",
        address: "U04-DMA02-V-APIA PARK-B1",
        email: "john.doe@example.com",
        phone: "+685 123 4567",
      },
      account: {
        meter_number: "166199",
        location: "U04-DMA02-V-APIA PARK-B1",
        service_period: "03/12/2024 - 28/02/2025",
        account_number: "12019",
        payment_history: [
          { date: "15/01/2025", amount: "$45.00" },
          { date: "12/12/2024", amount: "$38.50" },
        ],
      },
      meter: {
        previous_reading: "595.0",
        current_reading: "625.0",
        consumption: "30.0",
      },
      bill: {
        invoice_number: "INV_NO_35676",
        statement_date: "24/02/2025",
        due_date: "24/02/2025",
        amount_due: "29.25",
        balance_forward: "0.0",
        current_charges: "29.25",
        total_charges: "29.25",
        charges: [
          {
            description: "Water",
            consumption: "15.0",
            rate: "0.61",
            amount: "9.15",
          },
          {
            description: "Water",
            consumption: "15.0",
            rate: "1.34",
            amount: "20.1",
          },
        ],
      },
    };

    let html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <style>
        body { margin: 0; padding: 20px; font-family: Arial, sans-serif; background: #f5f5f5; }
        .preview-container { 
          position: relative; 
          width: ${currentPageSize.width}px; 
          height: ${currentPageSize.height}px; 
          background: white;
          margin: 0 auto;
          box-shadow: 0 0 10px rgba(0,0,0,0.1);
        }
        .component { position: absolute; box-sizing: border-box; overflow: hidden; }
      </style>
    </head>
    <body>
      <div class="preview-container">
  `;

    sortedComponents.forEach((component) => {
      if (!component.isVisible) return;

      const style = `
      left: ${component.x}px;
      top: ${component.y}px;
      width: ${component.width}px;
      height: ${component.height}px;
      z-index: ${component.zIndex};
      background-color: ${component.style.backgroundColor || "transparent"};
      border: ${component.style.borderWidth || 0}px ${
        component.style.borderStyle || "solid"
      } ${component.style.borderColor || "transparent"};
      border-radius: ${component.style.borderRadius || 0}px;
      padding: ${component.style.padding || 0}px;
      font-size: ${component.style.fontSize || 14}px;
      font-weight: ${component.style.fontWeight || "normal"};
      color: ${component.style.color || "#000000"};
      transform: rotate(${component.rotation}deg);
      text-align: ${component.style.textAlign || "left"};
    `;

      // Apply field mappings with sample data
      let componentHtml = component.content.htmlData || "";
      const mappings = componentConfig[component.id]?.fields || {};

      // Replace placeholders with sample data
      Object.entries(mappings).forEach(([field, mapping]: [string, any]) => {
        if (mapping.is_active && mapping.parameter) {
          const value = getNestedValue(sampleData, mapping.parameter);
          componentHtml = componentHtml.replace(
            new RegExp(`{{\\s*${field}\\s*}}`, "g"),
            value || `[${mapping.label || field}]`
          );
        }
      });

      // Replace any remaining placeholders
      componentHtml = componentHtml.replace(
        /\{\{([^}]+)\}\}/g,
        (match, field) => {
          return `<span style="background: #e3f2fd; padding: 2px 4px; border-radius: 3px;">[${field.trim()}]</span>`;
        }
      );

      html += `<div class="component" style="${style}">${componentHtml}</div>`;
    });

    html += `
      </div>
    </body>
    </html>
  `;

    return html;
  };

  // Helper function to get nested values
  const getNestedValue = (obj: any, path: string): any => {
    return path.split(".").reduce((curr, key) => curr?.[key], obj);
  };

  const handleSave = async () => {
    try {
      if (!selectedTemplateId && !existingTemplateId) {
        alert("Please select a base template first");
        return;
      }

      if (!localTemplateName.trim()) {
        alert("Please enter a template name");
        return;
      }

      // Update component config with field mappings before saving
      const configToSave = { ...componentConfig };
      components.forEach((component) => {
        if (!configToSave[component.id]) {
          configToSave[component.id] = {
            style: component.style,
            fields: {},
          };
        } else {
          configToSave[component.id].style = component.style;
        }
      });

      setComponentConfig(configToSave);

      if (existingTemplateId) {
        await updateTemplate();
        toast({title: "Template saved successfully!"});
        navigate('/bx/bill-setup/billtemplate');
        logEvent("Template Updated", {
          templateType: selectedTemplateId,
        });
      } else {
        await saveTemplate();
        logEvent("Template Created", {
          templateType: selectedTemplateId
        });
        toast({title: "Template saved successfully!"});
        navigate('/bx/bill-setup/billtemplate');
      }
    } catch (error: any) {
      console.error("Error saving template:", error);
      alert(error.message || "Failed to save template");
    }
  };

  const handleExport = () => {
    const exportData = {
      templateName: localTemplateName,
      pageSize: currentPageSize,
      components: components.map((c) => ({
        ...c,
        content: "",
      })),
      componentConfig,
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${localTemplateName
      .replace(/[^a-z0-9]/gi, "_")
      .toLowerCase()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const selectedComp = components.find((c) => c.id === selectedComponent);

  if (isLoading || isLoadingDetail || isLoadingBaseTemplate) {
    return (
      <div className="flex items-center justify-center h-screen">
        Loading...
      </div>
    );
  }

  if (error || queryErrorDetail) {
    return (
      <div className="flex items-center justify-center h-screen text-red-500">
        Error: {error}
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-slate-50">
      {/* Header */}
      <div className="bg-white p-4 border-b">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Input
              value={localTemplateName}
              onChange={handleTemplateNameChange}
              className="text-lg font-semibold w-80"
              placeholder="Template Name"
            />
            <Select value={pageSize} onValueChange={setPageSize}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(PAGE_SIZES).map(([key, size]) => (
                  <SelectItem key={key} value={key}>
                    {size.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {pageSize === "custom" && (
              <div className="flex gap-2">
                <Input
                  type="number"
                  value={customWidth}
                  onChange={(e) => setCustomWidth(Number(e.target.value))}
                  placeholder="Width"
                  className="w-20"
                />
                <Input
                  type="number"
                  value={customHeight}
                  onChange={(e) => setCustomHeight(Number(e.target.value))}
                  placeholder="Height"
                  className="w-20"
                />
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <div className="flex border rounded-md">
              <Button
                variant={tool === "select" ? "default" : "ghost"}
                size="sm"
                onClick={() => setTool("select")}
                className="rounded-r-none"
              >
                <MousePointer size={16} />
                Select
              </Button>
              <Button
                variant={tool === "move" ? "default" : "ghost"}
                size="sm"
                onClick={() => setTool("move")}
                className="rounded-l-none"
              >
                <Move size={16} />
                Move
              </Button>
            </div>

            <div className="flex items-center space-x-2 border-l pl-2">
              <Button
                variant={showGrid ? "default" : "outline"}
                size="sm"
                onClick={() => setShowGrid(!showGrid)}
              >
                <Grid3X3 size={16} />
                Grid
              </Button>
              <Button
                variant={snapToGrid ? "default" : "outline"}
                size="sm"
                onClick={() => setSnapToGrid(!snapToGrid)}
              >
                Snap
              </Button>
            </div>

            <div className="flex rounded-md border">
              <Button
                variant={activeTab === "canvas" ? "default" : "ghost"}
                size="sm"
                onClick={() => setActiveTab("canvas")}
                className="rounded-r-none"
              >
                <Layout size={16} />
                Canvas
              </Button>
              <Button
                variant={activeTab === "preview" ? "default" : "ghost"}
                size="sm"
                onClick={() => setActiveTab("preview")}
                className="rounded-l-none"
              >
                <Eye size={16} />
                Preview
              </Button>
            </div>

            {activeTab === "preview" && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPreviewExpanded(!previewExpanded)}
              >
                {previewExpanded ? (
                  <Minimize2 size={16} />
                ) : (
                  <Maximize2 size={16} />
                )}
              </Button>
            )}

            <Button variant="outline" size="sm" onClick={handleExport}>
              <Download size={16} />
              Export
            </Button>

            <Button
              variant="default"
              size="sm"
              onClick={handleSave}
              disabled={isSaving || isUpdating}
            >
              <Save size={16} />
              {isSaving || isUpdating ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>

        <div className="mt-2 text-sm text-gray-600">
          <span className="font-medium">Tools:</span>
          <span className="ml-2">
            Select tool to click and edit • Move tool to drag components • Hold
            Shift while dragging to move in Select mode
          </span>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden">
        <ResizablePanelGroup direction="horizontal">
          {/* Component Library */}
          <ResizablePanel defaultSize={20} minSize={15} maxSize={30}>
            <div className="h-full bg-white border-r">
              <div className="p-4 border-b">
                <h3 className="font-semibold text-gray-800 mb-3">
                  Template Selection
                </h3>
                <Select
                  value={selectedTemplateId}
                  onValueChange={handleTemplateSelect}
                  disabled={isLoadingBaseTemplate || isLoadingDetail}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a template..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTemplates.map((template: any) => (
                      <SelectItem
                        key={template.id}
                        value={template.id.toString()}
                      >
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {isLoadingBaseTemplate && (
                  <p className="text-sm text-gray-500 mt-2">
                    Loading template components...
                  </p>
                )}
              </div>

              <div className="p-4 ">
                <h3 className="font-semibold">Components</h3>
              </div>

              <ScrollArea className="h-[calc(100%-160px)]">
                <div className="p-4 space-y-2">
                  {availableComponents.length > 0 ? (
                    availableComponents.map((comp: any) => (
                      <Button
                        key={comp.id}
                        variant="outline"
                        className="w-full justify-start"
                        onClick={() => addComponent(comp)}
                      >
                        <span className="mr-2">{comp.icon || "📄"}</span>
                        {comp.componentName}
                      </Button>
                    ))
                  ) : (
                    <div className="text-center text-gray-500 py-8">
                      <p className="text-sm">
                        Select a template above to see available components
                      </p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </div>
          </ResizablePanel>

          <ResizableHandle withHandle />

          {/* Canvas/Preview */}
          <ResizablePanel
            defaultSize={activeTab === "canvas" && !previewExpanded ? 55 : 80}
          >
            <div className="h-full bg-gray-100 overflow-auto">
              {activeTab === "canvas" ? (
                <div className="p-8">
                  <div
                    ref={canvasRef}
                    className="relative mx-auto bg-white shadow-lg"
                    style={{
                      width: currentPageSize.width,
                      height: currentPageSize.height,
                      backgroundImage: showGrid
                        ? "radial-gradient(circle, #ddd 1px, transparent 1px)"
                        : "none",
                      backgroundSize: showGrid ? "10px 10px" : "auto",
                      cursor: tool === "move" ? "move" : "crosshair",
                    }}
                    onClick={handleCanvasClick}
                  >
                    {components.length === 0 &&
                      availableComponents.length === 0 &&
                      (!isLoadingBaseTemplate || !isLoadingDetail) && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-center text-gray-400">
                            <Layout
                              size={48}
                              className="mx-auto mb-4 opacity-50"
                            />
                            <p className="text-lg">
                              Select a template from the left panel to begin
                            </p>
                          </div>
                        </div>
                      )}

                    {(isLoadingBaseTemplate || isLoadingDetail) && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
                        <div className="text-center">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                          <p className="text-gray-600">
                            Loading template layout...
                          </p>
                        </div>
                      </div>
                    )}
                    {components.map((component) => (
                      <div
                        key={component.id}
                        className={`absolute transition-all duration-150 ${
                          selectedComponent === component.id
                            ? "ring-2 ring-blue-500 ring-offset-1"
                            : "hover:ring-2 hover:ring-blue-300 hover:ring-offset-1"
                        } ${
                          component.isLocked
                            ? "cursor-not-allowed"
                            : tool === "move"
                            ? "cursor-move"
                            : "cursor-pointer"
                        }`}
                        style={{
                          left: component.x,
                          top: component.y,
                          width: component.width,
                          height: component.height,
                          zIndex: component.zIndex,
                          display: component.isVisible ? "block" : "none",
                          backgroundColor: component.style.backgroundColor,
                          border: `${component.style.borderWidth}px ${component.style.borderStyle} ${component.style.borderColor}`,
                          borderRadius: `${component.style.borderRadius}px`,
                          padding: `${component.style.padding}px`,
                          fontSize: `${component.style.fontSize}px`,
                          fontWeight: component.style.fontWeight,
                          color: component.style.color,
                          transform: `rotate(${component.rotation}deg)`,
                          textAlign: component.style.textAlign,
                        }}
                        onClick={(e) => handleComponentClick(e, component.id)}
                        onMouseDown={(e) => handleMouseDown(e, component.id)}
                      >
                        {/* Component Content */}
                        <div
                          className="w-full h-full overflow-hidden select-none"
                          dangerouslySetInnerHTML={{
                            __html: component.content.htmlData || "",
                          }}
                        />

                        {/* Component Controls */}
                        {selectedComponent === component.id &&
                          !component.isLocked && (
                            <>
                              {/* Resize Handles */}
                              <div
                                className="absolute w-3 h-3 bg-blue-500 rounded-full -right-1.5 -bottom-1.5 cursor-se-resize"
                                onMouseDown={(e) =>
                                  handleMouseDown(e, component.id, true, "se")
                                }
                              />
                              <div
                                className="absolute w-3 h-3 bg-blue-500 rounded-full -left-1.5 -bottom-1.5 cursor-sw-resize"
                                onMouseDown={(e) =>
                                  handleMouseDown(e, component.id, true, "sw")
                                }
                              />
                              <div
                                className="absolute w-3 h-3 bg-blue-500 rounded-full -right-1.5 -top-1.5 cursor-ne-resize"
                                onMouseDown={(e) =>
                                  handleMouseDown(e, component.id, true, "ne")
                                }
                              />
                              <div
                                className="absolute w-3 h-3 bg-blue-500 rounded-full -left-1.5 -top-1.5 cursor-nw-resize"
                                onMouseDown={(e) =>
                                  handleMouseDown(e, component.id, true, "nw")
                                }
                              />

                              {/* Action Buttons */}
                              <div className="absolute -top-8 right-0 flex items-center space-x-1 bg-white shadow-md rounded px-1 py-0.5">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateComponent(component.id, {
                                      isVisible: !component.isVisible,
                                    });
                                  }}
                                  className="p-1 hover:bg-gray-100 rounded"
                                  title={component.isVisible ? "Hide" : "Show"}
                                >
                                  {component.isVisible ? (
                                    <Eye size={14} />
                                  ) : (
                                    <EyeOff size={14} />
                                  )}
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateComponent(component.id, {
                                      isLocked: !component.isLocked,
                                    });
                                  }}
                                  className="p-1 hover:bg-gray-100 rounded"
                                  title={component.isLocked ? "Unlock" : "Lock"}
                                >
                                  {component.isLocked ? (
                                    <Lock size={14} />
                                  ) : (
                                    <Unlock size={14} />
                                  )}
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedComponentForMapping(
                                      component.id
                                    );
                                    setShowFieldMapping(true);
                                  }}
                                  className="p-1 hover:bg-gray-100 rounded"
                                  title="Field Mapping"
                                >
                                  <FileText size={14} />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    duplicateComponent(component.id);
                                  }}
                                  className="p-1 hover:bg-gray-100 rounded"
                                  title="Duplicate"
                                >
                                  <Copy size={14} />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateComponent(component.id, {
                                      rotation: (component.rotation + 90) % 360,
                                    });
                                  }}
                                  className="p-1 hover:bg-gray-100 rounded"
                                  title="Rotate"
                                >
                                  <RotateCcw size={14} />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteComponent(component.id);
                                  }}
                                  className="p-1 hover:bg-gray-100 rounded text-red-500"
                                  title="Delete"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </>
                          )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div
                  className={`${
                    previewExpanded ? "p-8" : "p-4"
                  } bg-gray-100 h-full`}
                >
                  <iframe
                    ref={previewRef}
                    className="w-full h-full border-0 rounded-lg shadow-lg bg-white"
                    title="Template Preview"
                  />
                </div>
              )}
            </div>
          </ResizablePanel>

          <ResizableHandle withHandle />

          {activeTab === "canvas" && !previewExpanded && (
            <>
              {/* Properties Panel */}
              <ResizablePanel defaultSize={20} minSize={20} maxSize={35}>
                <div className="h-full bg-white border-l">
                  <div className="p-4 border-b">
                    <h3 className="font-semibold">Properties</h3>
                  </div>
                  <ScrollArea className="h-[calc(100%-60px)]">
                    <div className="p-4">
                      {selectedComp ? (
                        <div className="space-y-4">
                          {/* Component Info */}
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-2">
                              Component
                            </h4>
                            <p className="text-sm text-gray-600">
                              {selectedComp.type}
                            </p>
                          </div>

                          <Separator />

                          {/* Position & Size */}
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-2">
                              Position & Size
                            </h4>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Label htmlFor="x" className="text-xs">
                                  X
                                </Label>
                                <Input
                                  id="x"
                                  type="number"
                                  value={selectedComp.x}
                                  onChange={(e) =>
                                    updateComponent(selectedComp.id, {
                                      x: Number(e.target.value),
                                    })
                                  }
                                  className="h-8"
                                />
                              </div>
                              <div>
                                <Label htmlFor="y" className="text-xs">
                                  Y
                                </Label>
                                <Input
                                  id="y"
                                  type="number"
                                  value={selectedComp.y}
                                  onChange={(e) =>
                                    updateComponent(selectedComp.id, {
                                      y: Number(e.target.value),
                                    })
                                  }
                                  className="h-8"
                                />
                              </div>
                              <div>
                                <Label htmlFor="width" className="text-xs">
                                  Width
                                </Label>
                                <Input
                                  id="width"
                                  type="number"
                                  value={selectedComp.width}
                                  onChange={(e) =>
                                    updateComponent(selectedComp.id, {
                                      width: Number(e.target.value),
                                    })
                                  }
                                  className="h-8"
                                />
                              </div>
                              <div>
                                <Label htmlFor="height" className="text-xs">
                                  Height
                                </Label>
                                <Input
                                  id="height"
                                  type="number"
                                  value={selectedComp.height}
                                  onChange={(e) =>
                                    updateComponent(selectedComp.id, {
                                      height: Number(e.target.value),
                                    })
                                  }
                                  className="h-8"
                                />
                              </div>
                            </div>
                            <div className="mt-2">
                              <Label htmlFor="zIndex" className="text-xs">
                                Z-Index
                              </Label>
                              <Input
                                id="zIndex"
                                type="number"
                                value={selectedComp.zIndex}
                                onChange={(e) =>
                                  updateComponent(selectedComp.id, {
                                    zIndex: Number(e.target.value),
                                  })
                                }
                                className="h-8"
                                min="1"
                              />
                            </div>
                            <div className="mt-2">
                              <Label htmlFor="rotation" className="text-xs">
                                Rotation (degrees)
                              </Label>
                              <Input
                                id="rotation"
                                type="number"
                                value={selectedComp.rotation}
                                onChange={(e) =>
                                  updateComponent(selectedComp.id, {
                                    rotation: Number(e.target.value),
                                  })
                                }
                                className="h-8"
                                min="0"
                                max="360"
                                step="15"
                              />
                            </div>
                          </div>

                          <Separator />

                          {/* Style Properties */}
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-2">
                              Style
                            </h4>
                            <div className="space-y-2">
                              <div>
                                <Label htmlFor="bgColor" className="text-xs">
                                  Background Color
                                </Label>
                                <div className="flex items-center space-x-2">
                                  <input
                                    id="bgColor"
                                    type="color"
                                    value={
                                      selectedComp.style.backgroundColor ||
                                      "#ffffff"
                                    }
                                    onChange={(e) =>
                                      updateComponentStyle(selectedComp.id, {
                                        backgroundColor: e.target.value,
                                      })
                                    }
                                    className="w-8 h-8 border rounded cursor-pointer"
                                  />
                                  <Input
                                    type="text"
                                    value={
                                      selectedComp.style.backgroundColor ||
                                      "#ffffff"
                                    }
                                    onChange={(e) =>
                                      updateComponentStyle(selectedComp.id, {
                                        backgroundColor: e.target.value,
                                      })
                                    }
                                    className="flex-1 h-8"
                                  />
                                </div>
                              </div>
                              <div>
                                <Label
                                  htmlFor="borderColor"
                                  className="text-xs"
                                >
                                  Border Color
                                </Label>
                                <div className="flex items-center space-x-2">
                                  <input
                                    id="borderColor"
                                    type="color"
                                    value={
                                      selectedComp.style.borderColor ||
                                      "#e5e7eb"
                                    }
                                    onChange={(e) =>
                                      updateComponentStyle(selectedComp.id, {
                                        borderColor: e.target.value,
                                      })
                                    }
                                    className="w-8 h-8 border rounded cursor-pointer"
                                  />
                                  <Input
                                    type="text"
                                    value={
                                      selectedComp.style.borderColor ||
                                      "#e5e7eb"
                                    }
                                    onChange={(e) =>
                                      updateComponentStyle(selectedComp.id, {
                                        borderColor: e.target.value,
                                      })
                                    }
                                    className="flex-1 h-8"
                                  />
                                </div>
                              </div>
                              <div>
                                <Label
                                  htmlFor="borderWidth"
                                  className="text-xs"
                                >
                                  Border Width
                                </Label>
                                <Input
                                  id="borderWidth"
                                  type="number"
                                  value={selectedComp.style.borderWidth || 0}
                                  onChange={(e) =>
                                    updateComponentStyle(selectedComp.id, {
                                      borderWidth: Number(e.target.value),
                                    })
                                  }
                                  className="h-8"
                                  min="0"
                                  max="10"
                                />
                              </div>
                              <div>
                                <Label
                                  htmlFor="borderStyle"
                                  className="text-xs"
                                >
                                  Border Style
                                </Label>
                                <Select
                                  value={
                                    selectedComp.style.borderStyle || "solid"
                                  }
                                  onValueChange={(value) =>
                                    updateComponentStyle(selectedComp.id, {
                                      borderStyle: value,
                                    })
                                  }
                                >
                                  <SelectTrigger
                                    id="borderStyle"
                                    className="h-8"
                                  >
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="solid">Solid</SelectItem>
                                    <SelectItem value="dashed">
                                      Dashed
                                    </SelectItem>
                                    <SelectItem value="dotted">
                                      Dotted
                                    </SelectItem>
                                    <SelectItem value="double">
                                      Double
                                    </SelectItem>
                                    <SelectItem value="none">None</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <Label
                                  htmlFor="borderRadius"
                                  className="text-xs"
                                >
                                  Border Radius
                                </Label>
                                <Input
                                  id="borderRadius"
                                  type="number"
                                  value={selectedComp.style.borderRadius || 0}
                                  onChange={(e) =>
                                    updateComponentStyle(selectedComp.id, {
                                      borderRadius: Number(e.target.value),
                                    })
                                  }
                                  className="h-8"
                                  min="0"
                                  max="50"
                                />
                              </div>
                              <div>
                                <Label htmlFor="padding" className="text-xs">
                                  Padding
                                </Label>
                                <Input
                                  id="padding"
                                  type="number"
                                  value={selectedComp.style.padding || 0}
                                  onChange={(e) =>
                                    updateComponentStyle(selectedComp.id, {
                                      padding: Number(e.target.value),
                                    })
                                  }
                                  className="h-8"
                                  min="0"
                                  max="50"
                                />
                              </div>
                            </div>
                          </div>

                          <Separator />

                          {/* Text Properties */}
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-2">
                              Text
                            </h4>
                            <div className="space-y-2">
                              <div>
                                <Label htmlFor="fontSize" className="text-xs">
                                  Font Size
                                </Label>
                                <Input
                                  id="fontSize"
                                  type="number"
                                  value={selectedComp.style.fontSize || 14}
                                  onChange={(e) =>
                                    updateComponentStyle(selectedComp.id, {
                                      fontSize: Number(e.target.value),
                                    })
                                  }
                                  className="h-8"
                                  min="8"
                                  max="72"
                                />
                              </div>
                              <div>
                                <Label htmlFor="fontWeight" className="text-xs">
                                  Font Weight
                                </Label>
                                <Select
                                  value={
                                    selectedComp.style.fontWeight || "normal"
                                  }
                                  onValueChange={(value) =>
                                    updateComponentStyle(selectedComp.id, {
                                      fontWeight: value,
                                    })
                                  }
                                >
                                  <SelectTrigger
                                    id="fontWeight"
                                    className="h-8"
                                  >
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="normal">
                                      Normal
                                    </SelectItem>
                                    <SelectItem value="bold">Bold</SelectItem>
                                    <SelectItem value="100">Thin</SelectItem>
                                    <SelectItem value="300">Light</SelectItem>
                                    <SelectItem value="500">Medium</SelectItem>
                                    <SelectItem value="700">Bold</SelectItem>
                                    <SelectItem value="900">Black</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <Label htmlFor="textColor" className="text-xs">
                                  Text Color
                                </Label>
                                <div className="flex items-center space-x-2">
                                  <input
                                    id="textColor"
                                    type="color"
                                    value={
                                      selectedComp.style.color || "#374151"
                                    }
                                    onChange={(e) =>
                                      updateComponentStyle(selectedComp.id, {
                                        color: e.target.value,
                                      })
                                    }
                                    className="w-8 h-8 border rounded cursor-pointer"
                                  />
                                  <Input
                                    type="text"
                                    value={
                                      selectedComp.style.color || "#374151"
                                    }
                                    onChange={(e) =>
                                      updateComponentStyle(selectedComp.id, {
                                        color: e.target.value,
                                      })
                                    }
                                    className="flex-1 h-8"
                                  />
                                </div>
                              </div>
                              <div>
                                <Label htmlFor="textAlign" className="text-xs">
                                  Text Align
                                </Label>
                                <Select
                                  value={selectedComp.style.textAlign || "left"}
                                  onValueChange={(
                                    value:
                                      | "left"
                                      | "center"
                                      | "right"
                                      | "justify"
                                  ) =>
                                    updateComponentStyle(selectedComp.id, {
                                      textAlign: value,
                                    })
                                  }
                                >
                                  <SelectTrigger id="textAlign" className="h-8">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="left">Left</SelectItem>
                                    <SelectItem value="center">
                                      Center
                                    </SelectItem>
                                    <SelectItem value="right">Right</SelectItem>
                                    <SelectItem value="justify">
                                      Justify
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          </div>

                          <Separator />

                          {/* Quick Actions */}
                          <div className="pt-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedComponentForMapping(selectedComp.id);
                                setShowFieldMapping(true);
                              }}
                              className="w-full"
                            >
                              <FileText size={16} />
                              Configure Field Mapping
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center text-gray-500 mt-8">
                          <Maximize2
                            size={48}
                            className="mx-auto mb-4 opacity-50"
                          />
                          <p>Select a component to edit its properties</p>
                          <p className="text-xs mt-2">
                            Click on a component in the canvas
                          </p>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </div>
              </ResizablePanel>
            </>
          )}
        </ResizablePanelGroup>
      </div>

      {/* Field Mapping Dialog */}
      {showFieldMapping && selectedComponentForMapping && (
        <FieldMappingDialog
          isOpen={showFieldMapping}
          onClose={() => {
            setShowFieldMapping(false);
            setSelectedComponentForMapping(null);
          }}
          componentId={selectedComponentForMapping}
          componentName={
            components.find((c) => c.id === selectedComponentForMapping)
              ?.type || ""
          }
          currentMappings={
            componentConfig[selectedComponentForMapping]?.fields || {}
          }
          availableFields={extractFieldsFromHtml(
            componentConfig[selectedComponentForMapping]?.fields
          )}
          onSave={handleFieldMapping}
        />
      )}
    </div>
  );
};

export default BillCanvasDesigner;
